/**
 * Cache utility functions for managing sessionStorage caches
 */

/**
 * Invalidates the upcoming matches cache on the homepage
 * This should be called whenever a user submits or updates a prediction
 * to ensure the homepage shows the most current prediction status.
 * 
 * Also called when users login/register to clear any cached general matches
 * and show personalized matches for the authenticated user.
 */
export const invalidateUpcomingMatchesCache = (): void => {
  try {
    // Remove all upcoming matches related cache items
    sessionStorage.removeItem('upcoming_matches_data');
    sessionStorage.removeItem('upcoming_matches_by_season_data');
    sessionStorage.removeItem('upcoming_matches_timestamp');
    
    console.log('Upcoming matches cache invalidated');
  } catch (error) {
    console.error('Error invalidating upcoming matches cache:', error);
  }
};

/**
 * Invalidates seasons-related caches
 * This should be called when season data changes (like order updates)
 */
export const invalidateSeasonsCache = (): void => {
  try {
    // Seasons cache
    const seasonKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('seasons') || key.includes('active_seasons') ||
      key.includes('seasons_list') || key.includes('leaderboards_seasons') ||
      key.includes('partial_leaderboard_seasons')
    );
    seasonKeys.forEach(key => sessionStorage.removeItem(key));
    
    console.log('Seasons cache invalidated');
  } catch (error) {
    console.error('Error invalidating seasons cache:', error);
  }
};

/**
 * Invalidates all user-related caches
 * This is a comprehensive cache clearing function for logout scenarios
 */
export const invalidateAllUserCaches = (): void => {
  try {
    // Upcoming matches cache (Home page)
    sessionStorage.removeItem('upcoming_matches_data');
    sessionStorage.removeItem('upcoming_matches_by_season_data');
    sessionStorage.removeItem('upcoming_matches_timestamp');
    
    // Leagues cache (Leagues page)
    sessionStorage.removeItem('leagues_data');
    sessionStorage.removeItem('user_leagues_data');
    sessionStorage.removeItem('seasons_data');
    sessionStorage.removeItem('leagues_cache_timestamp');
    
    // Home page league caches
    sessionStorage.removeItem('home_user_leagues_data');
    sessionStorage.removeItem('home_user_leagues_timestamp');
    sessionStorage.removeItem('home_main_user_leagues_data');
    sessionStorage.removeItem('home_main_user_leagues_timestamp');
    sessionStorage.removeItem('home_active_seasons_data');
    sessionStorage.removeItem('home_active_seasons_timestamp');
    
    // TeamWidget cache
    const teamWidgetKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('global_rank') || key.includes('team_widget')
    );
    teamWidgetKeys.forEach(key => sessionStorage.removeItem(key));
    
    // User predictions and statistics cache
    const predictionKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('predictions') || key.includes('user_stats') || 
      key.includes('user_predictions') || key.includes('my_predictions_summary')
    );
    predictionKeys.forEach(key => sessionStorage.removeItem(key));
    
    // News and posts cache
    const newsKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('news') || key.includes('posts') || 
      key.includes('latest_news') || key.includes('news_') || 
      key.includes('post_') || key.includes('listing')
    );
    newsKeys.forEach(key => sessionStorage.removeItem(key));
    
    // Leaderboards cache
    const leaderboardKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('leaderboard') || key.includes('partial_leaderboard') ||
      key.includes('leaderboards_seasons')
    );
    leaderboardKeys.forEach(key => sessionStorage.removeItem(key));
    
    // Seasons cache
    const seasonKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('seasons') || key.includes('active_seasons') ||
      key.includes('seasons_list')
    );
    seasonKeys.forEach(key => sessionStorage.removeItem(key));
    
    // Trophy cache (TrophyWidget component)
    const trophyKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('trophy_awards') || key.includes('trophies')
    );
    trophyKeys.forEach(key => sessionStorage.removeItem(key));
    
    console.log('All user caches invalidated');
  } catch (error) {
    console.error('Error invalidating user caches:', error);
  }
};
